import React from "react";
import { Alert, Input, Modal, Tag, message } from "antd";
import APIService from "../../Service/APIService";
import CommonButton from "../../common/button/button.js";
import ModalView from "../../common/modal/index.js";
import Text from "../../common/typography/text.js";
import "./AccountUpdateRequests.css";

const displayValue = (value) => {
  const normalized = String(value ?? "").trim();
  return normalized || "—";
};

const AccountUpdateRequestReviewModal = ({
  requestId,
  open,
  canReview,
  onClose,
  onReviewed,
}) => {
  const apiRef = React.useRef(new APIService());
  const [detail, setDetail] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [rejectMode, setRejectMode] = React.useState(false);
  const [rejectionReason, setRejectionReason] = React.useState("");

  const loadDetail = React.useCallback(async () => {
    if (!open || !requestId) return;
    setLoading(true);
    setError("");
    try {
      const response = await apiRef.current.getDataFromBackend(
        `getaccountupdaterequestbyid?ID=${encodeURIComponent(requestId)}`,
      );
      setDetail(response?.data?.data || null);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Unable to load the account update request.",
      );
    } finally {
      setLoading(false);
    }
  }, [open, requestId]);

  React.useEffect(() => {
    if (open) {
      setDetail(null);
      setRejectMode(false);
      setRejectionReason("");
      loadDetail();
    }
  }, [loadDetail, open]);

  const completeReview = async ({ action, acknowledgeDrift = false }) => {
    if (!detail?.ID || saving) return;
    setSaving(true);
    setError("");
    try {
      await apiRef.current.addDataToBackend(
        {
          RequestID: detail.ID,
          ...(action === "reject"
            ? { RejectionReason: rejectionReason.trim() }
            : {}),
          ...(action === "approve"
            ? { AcknowledgeDrift: acknowledgeDrift }
            : {}),
        },
        `updateaccountupdaterequest?type=${action}`,
      );
      message.success(
        action === "approve"
          ? "Account changes approved successfully."
          : "Account changes rejected.",
      );
      onReviewed?.();
      onClose?.();
    } catch (requestError) {
      const responseData = requestError?.response?.data;
      if (
        action === "approve" &&
        responseData?.code === "DRIFT_ACKNOWLEDGMENT_REQUIRED"
      ) {
        const refreshedDetail = {
          ...detail,
          Fields: responseData.fields || detail.Fields,
          HasDrifted: true,
          HasConflict: true,
        };
        setDetail(refreshedDetail);
        Modal.confirm({
          title: "Account data changed after submission",
          content:
            "Approving will overwrite the highlighted live values. Confirm that you reviewed the latest values.",
          okText: "Approve and overwrite",
          okButtonProps: { danger: true },
          onOk: () =>
            completeReview({ action: "approve", acknowledgeDrift: true }),
        });
        return;
      }
      if (responseData?.code === "ACCOUNT_UPDATE_REQUEST_ALREADY_REVIEWED") {
        await loadDetail();
      }
      setError(responseData?.message || "Unable to review this request.");
    } finally {
      setSaving(false);
    }
  };

  const approve = () => {
    if (detail?.HasConflict) {
      Modal.confirm({
        title: "Account data changed after submission",
        content:
          "Approving will overwrite the highlighted live values. Confirm that you reviewed the latest values.",
        okText: "Approve and overwrite",
        okButtonProps: { danger: true },
        onOk: () =>
          completeReview({ action: "approve", acknowledgeDrift: true }),
      });
      return;
    }
    completeReview({ action: "approve" });
  };

  const reject = () => {
    if (!rejectionReason.trim()) {
      setError("A rejection reason is required.");
      return;
    }
    completeReview({ action: "reject" });
  };

  const pending = detail?.Status === "Pending";

  const reviewContent = (
    <>
      {error ? <Alert type="error" showIcon message={error} /> : null}
      {!loading && detail ? (
        <div className="account-update-review">
          <div className="account-update-review-meta">
            <div>
              <Text variant="tinyText" color="lightText">
                Account
              </Text>
              <Text variant="smallText">
                {detail.Account?.AccountName || detail.AccountID}
              </Text>
            </div>
            <div>
              <Text variant="tinyText" color="lightText">
                Requester
              </Text>
              <Text variant="smallText">{detail.RequesterName}</Text>
            </div>
            <div>
              <Text variant="tinyText" color="lightText">
                Status
              </Text>
              <span>
                <Tag>{detail.Status}</Tag>
              </span>
            </div>
          </div>

          <section
            className="account-update-requester-notes"
            aria-labelledby="account-update-requester-notes-title"
          >
            <div id="account-update-requester-notes-title">
              <Text variant="tinyText" color="lightText">
                Requester notes
              </Text>
            </div>
            <div className="account-update-requester-notes-content">
              {String(detail.RequesterNotes ?? "").trim() ||
                "No notes provided"}
            </div>
          </section>

          {detail.HasConflict ? (
            <Alert
              type="warning"
              showIcon
              message="Live account values changed after this request was submitted."
              description="Conflicting rows require explicit overwrite confirmation before approval."
            />
          ) : detail.HasDrifted ? (
            <Alert
              type="info"
              showIcon
              message="Some live values changed, but they already match the requested values."
            />
          ) : null}

          <div className="account-update-diff-table-wrap">
            <table className="account-update-diff-table">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Original</th>
                  <th>Current</th>
                  <th>Requested</th>
                </tr>
              </thead>
              <tbody>
                {(detail.Fields || []).map((field) => (
                  <tr
                    key={field.Key}
                    className={
                      field.HasConflict
                        ? "conflict"
                        : field.HasDrifted
                          ? "drifted"
                          : ""
                    }
                  >
                    <td>{field.Label}</td>
                    <td>{displayValue(field.OriginalValue)}</td>
                    <td>{displayValue(field.CurrentValue)}</td>
                    <td>{displayValue(field.RequestedValue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {detail.Status === "Rejected" && detail.RejectionReason ? (
            <Alert
              type="warning"
              message="Rejection reason"
              description={detail.RejectionReason}
            />
          ) : null}

          {canReview && pending && rejectMode ? (
            <div className="account-update-reject-box">
              <label htmlFor="account-update-rejection-reason">
                Rejection reason
              </label>
              <Input.TextArea
                id="account-update-rejection-reason"
                value={rejectionReason}
                maxLength={2000}
                showCount
                rows={4}
                disabled={saving}
                onChange={(event) => setRejectionReason(event.target.value)}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );

  const reviewActions = (
    <div className="account-update-review-actions">
      <CommonButton
        variant="table"
        style={{ minWidth: "100px" }}
        disabled={saving}
        onClick={onClose}
      >
        Close
      </CommonButton>
      {canReview && pending && !rejectMode ? (
        <>
          <CommonButton
            variant="table"
            subVariant="failure"
            style={{ minWidth: "100px" }}
            disabled={saving}
            onClick={() => setRejectMode(true)}
          >
            Reject
          </CommonButton>
          <CommonButton
            variant="table"
            subVariant="success"
            style={{ minWidth: "100px" }}
            disabled={saving}
            onClick={approve}
          >
            {saving ? "Approving…" : "Approve"}
          </CommonButton>
        </>
      ) : null}
      {canReview && pending && rejectMode ? (
        <>
          <CommonButton
            variant="table"
            style={{ minWidth: "100px" }}
            disabled={saving}
            onClick={() => setRejectMode(false)}
          >
            Back
          </CommonButton>
          <CommonButton
            variant="table"
            subVariant="failure"
            style={{ minWidth: "140px" }}
            disabled={saving || !rejectionReason.trim()}
            onClick={reject}
          >
            {saving ? "Rejecting…" : "Confirm Rejection"}
          </CommonButton>
        </>
      ) : null}
    </div>
  );

  return (
    <ModalView
      isOpen={open}
      onClose={saving ? () => {} : onClose}
      isLoading={loading}
      minWidth="min(900px, calc(100vw - 32px))"
      maxWidth="min(900px, calc(100vw - 32px))"
      modalTitle="Account Update Request"
      modalSubTitle="Review the current and requested account values before taking action."
      textInputComponent={reviewContent}
      footerButtonWrapper={reviewActions}
      parentClassname="account-update-review-modal"
    />
  );
};

export default AccountUpdateRequestReviewModal;
